import type { JobStatus } from '@detachly/contracts'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { describeError } from '../lib/errors'

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'danger'
  block?: boolean
}

export function Button({ variant = 'primary', block, className, ...props }: ButtonProps) {
  const classes = [
    'button',
    variant === 'secondary' ? 'button--secondary' : '',
    variant === 'danger' ? 'button--danger' : '',
    block ? 'button--block' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  return <button type="button" {...props} className={classes} />
}

export function Card({ children, flat }: { children: ReactNode; flat?: boolean }) {
  return <section className={flat ? 'card card--flat' : 'card'}>{children}</section>
}

export function Metric({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="metric">
      <div className="metric__value">{value}</div>
      <div className="metric__label">{label}</div>
    </div>
  )
}

const STATUS_COPY: Record<JobStatus, { label: string; tone: string }> = {
  queued: { label: 'Queued', tone: '' },
  claimed: { label: 'Starting', tone: 'badge--running' },
  running: { label: 'Running', tone: 'badge--running' },
  succeeded: { label: 'Done', tone: 'badge--success' },
  failed: { label: 'Failed', tone: 'badge--failed' },
  canceled: { label: 'Canceled', tone: '' },
}

/** Status is spelled out as well as coloured — colour alone is not a status (WCAG). */
export function StatusBadge({ status }: { status: JobStatus }) {
  const { label, tone } = STATUS_COPY[status]
  return (
    <span className={`badge ${tone}`.trim()}>
      <span className="badge__dot" aria-hidden="true" />
      {label}
    </span>
  )
}

export function ErrorPanel({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { code, title, action } = describeError(error)

  return (
    <div className="notice notice--error" role="alert">
      <p className="notice__title">{title}</p>
      <p>{action}</p>
      <p className="subtle mono">{code}</p>
      {onRetry ? (
        <div>
          <Button variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : null}
    </div>
  )
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className="empty">
      <h2>{title}</h2>
      <p className="muted">{description}</p>
      {action}
    </div>
  )
}

export function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="stack" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="skeleton" style={{ width: `${100 - index * 12}%` }} />
      ))}
    </div>
  )
}

export function CodeInline({ children }: { children: ReactNode }) {
  return <code className="mono">{children}</code>
}
